const INTERNAL_ORIGIN = "http://fluxity.local";

const DISALLOWED_PREFIXES = ["/login", "/signup", "/auth/callback", "/auth/error"];

type TenantRole = "admin" | "member" | "viewer";

interface LandingOnboardingState {
  completedSteps: string[];
}

export function sanitizeNextPath(next: string | null | undefined): string | null {
  if (!next) return null;

  const candidate = next.trim();
  if (!candidate || !candidate.startsWith("/") || candidate.startsWith("//")) {
    return null;
  }

  try {
    const url = new URL(candidate, INTERNAL_ORIGIN);
    const normalized = `${url.pathname}${url.search}${url.hash}`;

    const blocked = DISALLOWED_PREFIXES.some(
      (prefix) =>
        normalized === prefix ||
        normalized.startsWith(`${prefix}/`) ||
        normalized.startsWith(`${prefix}?`) ||
        normalized.startsWith(`${prefix}#`)
    );

    return blocked ? null : normalized;
  } catch {
    return null;
  }
}

export function getPostAuthRedirect(next: string | null | undefined): string {
  return sanitizeNextPath(next) ?? "/dashboard";
}

/**
 * Choose the first useful destination after authentication. Explicit internal
 * destinations always win; the onboarding detour only applies to admins who
 * did not request a specific page.
 */
export function getTenantLandingPath({
  role,
  onboardingState,
  requestedNext,
}: {
  role: TenantRole;
  onboardingState: LandingOnboardingState | null;
  requestedNext: string | null | undefined;
}): string {
  const safeNext = sanitizeNextPath(requestedNext);
  if (safeNext) return safeNext;

  const onboardingComplete = onboardingState?.completedSteps.includes("done") ?? false;
  if (role === "admin" && !onboardingComplete) {
    return "/onboarding";
  }

  return "/dashboard";
}

export function appendSafeNextParam(
  path: string,
  next: string | null | undefined
): string {
  const safeNext = sanitizeNextPath(next);
  if (!safeNext) return path;

  const url = new URL(path, INTERNAL_ORIGIN);
  url.searchParams.set("next", safeNext);
  return `${url.pathname}${url.search}${url.hash}`;
}
